import mixpanel from 'mixpanel-browser';
import { trackingEvents } from './events';

const SENSITIVE_FIELD_KEYWORDS = {
  aadhaarNumber: 'aadhaarNumber',
  accessToken: 'accessToken',
  accountNumber: 'accountNumber',
  address: 'address',
  applicationNumber: 'applicationNumber',
  authorization: 'authorization',
  bgvUserData: 'bgvUserData',
  birthDate: 'birthDate',
  cin: 'cin',
  city: 'city',
  consumerId: 'consumerId',
  currentAddress: 'currentAddress',
  din: 'din',
  dlno: 'dlno',
  dob: 'dob',
  documentCode: 'documentCode',
  documentList: 'documentList',
  documentNumber: 'documentNumber',
  employeeCode: 'employeeCode',
  employeeName: 'employeeName',
  employerRefId: 'employerRefId',
  employmentDocumentType: 'employmentDocumentType',
  ewsNumber: 'ewsNumber',
  expiryDate: 'expiryDate',
  facebook: 'facebook',
  fileList: 'fileList',
  fileUrl: 'fileUrl',
  firstName: 'firstName',
  gst: 'gst',
  hrProvidedData: 'hrProvidedData',
  iec: 'iec',
  ifsc: 'ifsc',
  imapServiceName: 'imapServiceName',
  instagram: 'instagram',
  issuingState: 'issuingState',
  lastName: 'lastName',
  leiNo: 'leiNo',
  linkedin: 'linkedin',
  lpgId: 'lpgId',
  membershipNumber: 'membershipNumber',
  nameAsPerEmployerRecords: 'nameAsPerEmployerRecords',
  nationality: 'nationality',
  number: 'number',
  pan: 'pan',
  password: 'password',
  permanentAddress: 'permanentAddress',
  personalNumber: 'personalNumber',
  photo: 'photo',
  pincode: 'pincode',
  propertyNo: 'propertyNo',
  provider: 'provider',
  rationCardNumber: 'rationCardNumber',
  refreshToken: 'refreshToken',
  registrationNumber: 'registrationNumber',
  regNo: 'regNo',
  responseList: 'responseList',
  seqno: 'seqno',
  sessionId: 'sessionId',
  sex: 'sex',
  sourceId: 'sourceId',
  sourceImageUrl: 'sourceImageUrl',
  state: 'state',
  statementDocument: 'statementDocument',
  tan: 'tan',
  targetImageUrl: 'targetImageUrl',
  twitter: 'twitter',
  uan: 'uan',
  udyamNumber: 'udyamNumber',
  upi: 'upi',
  value: 'value',
  voterId: 'voterId',
  yearOfRegistration: 'yearOfRegistration'
};

// Use direct object lookup instead of array + includes
const isSensitiveKey = (key: string): boolean => {
  return Object.prototype.hasOwnProperty.call(SENSITIVE_FIELD_KEYWORDS, key);
};

// Recursively redact sensitive data from the data object considering it to be json object
const redactSensitiveData = (data: unknown): unknown => {
  if (data === null || data === undefined) return data;

  if (Array.isArray(data)) {
    return data.map((item) => redactSensitiveData(item));
  }

  if (typeof data === 'object') {
    const result: Record<string, unknown> = {};
    Object.entries(data as Record<string, unknown>).forEach(([key, value]) => {
      if (isSensitiveKey(key)) {
        result[key] = '[REDACTED]';
      } else {
        result[key] = redactSensitiveData(value);
      }
    });
    return result;
  }

  return data;
};

export { redactSensitiveData };

mixpanel?.init(import.meta.env.VITE_MIXPANEL_TOKEN ?? '', {
  // trackAutomaticEvents: true, //TODO: this is not in the config options
  autotrack: true,
  api_host: import.meta.env.VITE_MIXPANEL_API_HOST
});

// Wrap mixpanel methods to ensure all payloads are redacted before sending
const originalTrack = mixpanel.track.bind(mixpanel);

// eslint-disable-next-line @typescript-eslint/no-explicit-any
(mixpanel as any).track = (eventName: string, properties?: any, ...rest: any[]) => {
  const safeProperties = properties ? redactSensitiveData(properties) : properties;
  return originalTrack(eventName, safeProperties as any, ...rest);
};

if (mixpanel.people && typeof mixpanel.people.set === 'function') {
  const originalPeopleSet = mixpanel.people.set.bind(mixpanel.people);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  mixpanel.people.set = (props: any, ...rest: any[]) => {
    const safeProps = props ? redactSensitiveData(props) : props;
    return originalPeopleSet(safeProps as any, ...rest);
  };
}

if (typeof mixpanel.register === 'function') {
  const originalRegister = mixpanel.register.bind(mixpanel);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (mixpanel as any).register = (props: any, ...rest: any[]) => {
    const safeProps = props ? redactSensitiveData(props) : props;
    return originalRegister(safeProps as any, ...rest);
  };
}

export { mixpanel, trackingEvents };
